<script setup>
import { computed, onMounted, watch } from 'vue'
import { useStore } from 'vuex'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'

const props = defineProps({
  modelValue: {
    type: String,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  orgId: {
    type: [String, Number],
    required: true
  }
})

const emit = defineEmits(['update:modelValue'])
const store = useStore()
const availableModels = computed(() => store.getters['chat/getAvailableModels'])

const fetchModelsIfNeeded = async (orgId) => {
  if (!orgId) {
    console.warn('[ModelSelector] orgId is not available yet')
    return
  }

  if (availableModels.value.length === 0) {
    try {
      await store.dispatch('chat/fetchAvailableModels', { orgId })
    } catch (error) {
      console.error('[ModelSelector] Failed to load available models:', error)
    }
  }
}

onMounted(() => {
  fetchModelsIfNeeded(props.orgId)
})

watch(() => props.orgId, (newOrgId) => {
  if (newOrgId) {
    fetchModelsIfNeeded(newOrgId)
  }
}, { immediate: true })

const selectedModel = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})
</script>

<template>
  <Select v-model="selectedModel" :disabled="disabled">
    <SelectTrigger class="max-w-[150px]">
      <SelectValue>
        {{ availableModels.find(model => model.value === selectedModel)?.label || 'Select Model' }}
      </SelectValue>
    </SelectTrigger>
    <SelectContent>
      <SelectGroup>
        <SelectItem
          v-for="model in availableModels"
          :key="model.value"
          :value="model.value"
        >
          <div class="flex justify-between items-center">
            <div class="flex flex-col">
              <span class="font-medium text-sm">{{ model.label }}</span>
              <span class="text-xs text-muted-foreground">{{ model.description }}</span>
            </div>
            <div class="ml-3">
              <Badge variant="secondary" v-if="model.value !== selectedModel">New Chat</Badge>
            </div>
          </div>
        </SelectItem>
      </SelectGroup>
    </SelectContent>
  </Select>
</template>
